import type { FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowRight, Menu, Phone, X } from 'lucide-react'
import { BrandLockup } from '@/components/generals/brand-mark'
import { ButtonLink } from '@/components/generals/button'
import { Container } from '@/components/generals/container'
import { OpeningStatusChip, OpeningStatusLine } from '@/components/generals/opening-status'

import { garage, navLinks } from '@/constants/garage'

export const SiteHeader: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)
    const toggleRef = useRef<HTMLButtonElement>(null)
    const firstLinkRef = useRef<HTMLAnchorElement>(null)

    const close = useCallback((restoreFocus = false) => {
        setOpen(false)

        if (restoreFocus) toggleRef.current?.focus()
    }, [])

    useEffect(() => {
        if (!open) return

        firstLinkRef.current?.focus()

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close(true)
        }

        const desktop = window.matchMedia('(min-width: 1280px)')
        const onChange = () => {
            if (desktop.matches) close()
        }

        window.addEventListener('keydown', onKeyDown)
        desktop.addEventListener('change', onChange)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            desktop.removeEventListener('change', onChange)
        }
    }, [open, close])

    return (
        <header className='sticky top-0 z-40 border-b border-border bg-background'>
            <Container className='flex h-16 items-center gap-2 max-sm:px-4 lg:h-20 lg:gap-6'>
                <a
                    href='#top'
                    className='-ml-1 flex min-w-0 items-center rounded-md px-1 py-1'
                    aria-label={`${garage.name}, retour en haut de page`}
                >
                    <BrandLockup compact />
                </a>

                <nav aria-label='Navigation principale' className='hidden flex-1 justify-center xl:flex'>
                    <ul className='flex items-center gap-1 xl:gap-2'>
                        {navLinks.map(link => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    className='inline-flex h-11 items-center rounded-md px-3 text-[0.9375rem] font-medium text-foreground/80 transition-colors duration-150 hover:bg-foreground/[0.05] hover:text-foreground'
                                >
                                    {link.short}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2 xl:ml-0'>
                    <OpeningStatusChip />

                    <ButtonLink
                        href={garage.phone.href}
                        size='icon'
                        className='md:hidden'
                        aria-label={`Appeler le garage au ${garage.phone.display}`}
                    >
                        <Phone aria-hidden='true' className='size-5' />
                    </ButtonLink>

                    <ButtonLink href={garage.phone.href} className='hidden tabular-nums md:inline-flex'>
                        <Phone aria-hidden='true' className='size-4' />
                        {garage.phone.display}
                    </ButtonLink>

                    <button
                        ref={toggleRef}
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='menu-mobile'
                        className='inline-flex size-12 items-center justify-center rounded-md border border-border text-foreground transition-colors duration-150 hover:border-foreground/40 xl:hidden'
                    >
                        {open ? <X aria-hidden='true' className='size-5' /> : <Menu aria-hidden='true' className='size-5' />}

                        <span className='sr-only'>
                            {open ? 'Fermer le menu' : 'Ouvrir le menu'}
                        </span>
                    </button>
                </div>
            </Container>

            <div
                id='menu-mobile'
                hidden={!open}
                className='absolute inset-x-0 top-full z-40 border-b border-border bg-background shadow-float xl:hidden'
            >
                <Container className='pb-6 pt-2 max-sm:px-4'>
                    <nav aria-label='Navigation mobile'>
                        <ul className='divide-y divide-border'>
                            {navLinks.map((link, index) => (
                                <li key={link.href}>
                                    <a
                                        ref={index === 0 ? firstLinkRef : undefined}
                                        href={link.href}
                                        onClick={() => close()}
                                        className='flex min-h-14 items-center gap-4 py-2 text-lg font-semibold'
                                    >
                                        <span className='label-mono w-6 text-signal-text'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>

                                        <span className='flex-1'>
                                            {link.label}
                                        </span>

                                        <ArrowRight aria-hidden='true' className='size-5 text-muted-foreground' />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='mt-4 rounded-lg border border-border bg-card p-4'>
                        <OpeningStatusLine />

                        <p className='mt-1 text-sm text-muted-foreground'>
                            {garage.address.street}, {garage.address.postalCode} {garage.address.city}
                        </p>

                        <ButtonLink href={garage.phone.href} size='lg' className='mt-4 w-full tabular-nums'>
                            <Phone aria-hidden='true' className='size-5' />
                            Appeler le {garage.phone.display}
                        </ButtonLink>
                    </div>
                </Container>
            </div>

            {open && (
                <button
                    type='button'
                    tabIndex={-1}
                    aria-hidden='true'
                    onClick={() => close()}
                    className='fixed inset-x-0 bottom-0 top-16 -z-10 lg:top-20 cursor-default bg-ink/30 xl:hidden'
                />
            )}
        </header>
    )
}
